/**
 * State Management Module
 * Centralized application state for multi-region, multi-area support
 *
 * Supports two navigation modes:
 * 1. Spot-based (new): Spot slug → conditions (e.g., 'blacks-beach')
 * 2. Station-based (legacy): Region → Area → Station hierarchy
 */

import { logger } from './logger.js';
import { DEFAULTS, DEFAULT_COORDS, DEFAULT_WEATHER_POINT, DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM } from '../shared/defaults.mjs';

// Application state - single source of truth
export const AppState = {
    // Mode flag
    useSpotMode: false,         // Whether we're using spot-based navigation

    // Spot-based navigation (new)
    currentSpot: null,          // Full spot object from API
    currentSpotSlug: null,      // URL-friendly slug (e.g., 'blacks-beach')
    spotsCache: null,           // Cached list of all spots from API

    // Hierarchy levels (legacy)
    currentRegion: null,        // Top-level region (e.g., California)
    currentArea: null,          // Mid-level area (e.g., San Diego County)
    currentStation: null,       // Station object with all metadata
    currentStationId: null,     // Station ID string (e.g., '9410170')

    // Loaded configuration
    regions: {},                // All regions from regions.json
    legacyMapping: {},          // Legacy region ID → new hierarchy mapping

    // State flags
    initialized: false
};

// ----- Spot-based navigation (new) -----

/**
 * Set the current spot by slug
 * @param {Object} spot - Full spot object from API
 */
export function setCurrentSpot(spot) {
    // Note: Don't set useSpotMode here - this function is also used to attach
    // spot metadata for rating calculations without changing navigation mode.
    // useSpotMode should only be set in initializeSpotState() for spot-based nav.
    // Also don't overwrite currentRegion/currentArea - they contain full hierarchy
    // data needed for station navigation in legacy mode.
    AppState.currentSpot = spot;
    AppState.currentSpotSlug = spot?.slug || null;
}

/**
 * Get the current spot slug
 */
export function getCurrentSpotSlug() {
    return AppState.currentSpotSlug;
}

/**
 * Get the current spot object
 */
export function getCurrentSpot() {
    return AppState.currentSpot;
}

/**
 * Check if we're in spot-based mode
 */
export function isSpotMode() {
    return AppState.useSpotMode;
}

/**
 * Set the cached spots list
 */
export function setSpotsCache(spots) {
    AppState.spotsCache = spots;
}

/**
 * Get the cached spots list
 */
export function getSpotsCache() {
    return AppState.spotsCache;
}

/**
 * Get spot info from cache by slug
 */
export function getSpotFromCache(slug) {
    if (!AppState.spotsCache?.spots) return null;
    return AppState.spotsCache.spots.find(s => s.slug === slug);
}

/**
 * Get grouped spots (by region/area) from cache
 */
export function getGroupedSpots() {
    return AppState.spotsCache?.grouped || {};
}

/**
 * Get spots for a specific region from cache
 */
export function getSpotsForRegion(region) {
    const grouped = getGroupedSpots();
    return grouped[region] || {};
}

/**
 * Get the swell window for the current spot
 */
export function getCurrentSwellWindow() {
    return AppState.currentSpot?.swellWindow || null;
}

/**
 * Get the ideal conditions for the current spot
 */
export function getCurrentIdealConditions() {
    return AppState.currentSpot?.idealConditions || null;
}

/**
 * Get buoy associations for the current spot
 */
export function getCurrentBuoyAssociations() {
    return AppState.currentSpot?.buoys || [];
}

// ----- Legacy station-based navigation -----

/**
 * Get the timezone for the current region
 * Falls back to Pacific time if not set
 */
export function getCurrentTimezone() {
    if (AppState.useSpotMode && AppState.currentSpot) {
        return AppState.currentSpot.timezone || 'America/Los_Angeles';
    }
    return AppState.currentRegion?.timezone || 'America/Los_Angeles';
}

/**
 * Get the buoy ID to use for the current area/station
 * Priority: spot buoys > station override > area primary > fallback default
 */
export function getCurrentBuoy() {
    // Spot-based: use first buoy from associations
    if (AppState.useSpotMode && AppState.currentSpot?.buoys?.length > 0) {
        return AppState.currentSpot.buoys[0].buoyId;
    }

    // Station-specific override takes precedence
    if (AppState.currentStation?.buoyOverride) {
        return AppState.currentStation.buoyOverride;
    }
    // Area's primary buoy (buoys now live at area level in v2)
    if (AppState.currentArea?.buoys?.primary) {
        return AppState.currentArea.buoys.primary;
    }
    // No buoy configured - return null to trigger Open-Meteo Marine fallback
    // using the spot's lat/lon instead of a wrong default buoy
    return null;
}

/**
 * Get fallback buoy IDs for retry logic
 */
export function getFallbackBuoys() {
    // Spot-based: use remaining buoys from associations
    if (AppState.useSpotMode && AppState.currentSpot?.buoys?.length > 1) {
        return AppState.currentSpot.buoys.slice(1).map(b => b.buoyId);
    }
    return AppState.currentArea?.buoys?.fallback || [];
}

/**
 * Get all areas for the current region
 */
export function getAreasForCurrentRegion() {
    return AppState.currentRegion?.areas || {};
}

/**
 * Get all stations for the current area
 */
export function getStationsForCurrentArea() {
    return AppState.currentArea?.stations || {};
}

/**
 * Get the current region ID
 */
export function getCurrentRegionId() {
    if (AppState.useSpotMode && AppState.currentSpot) {
        return AppState.currentSpot.region;
    }
    return AppState.currentRegion?.id || DEFAULTS.region;
}

/**
 * Get the current area ID
 */
export function getCurrentAreaId() {
    if (AppState.useSpotMode && AppState.currentSpot) {
        return AppState.currentSpot.area;
    }
    return AppState.currentArea?.id || null;
}

/**
 * Get the current location name for display
 */
export function getCurrentLocationName() {
    if (AppState.useSpotMode && AppState.currentSpot) {
        return AppState.currentSpot.name;
    }
    return AppState.currentStation?.name || 'San Diego'; // Keep human-readable name
}

/**
 * Get the effective station ID for tide predictions
 * Uses referenceStation if the current station is a subordinate station
 * (subordinate stations don't have direct NOAA predictions)
 */
export function getEffectiveTideStationId() {
    // Spot-based: use the NOAA station ID from spot config
    if (AppState.useSpotMode && AppState.currentSpot) {
        return AppState.currentSpot.noaaStationId;
    }

    if (!AppState.currentStation) {
        return AppState.currentStationId || DEFAULTS.station;
    }
    // If this station has a reference station, use that for predictions
    if (AppState.currentStation.referenceStation) {
        return AppState.currentStation.referenceStation;
    }
    return AppState.currentStationId;
}

/**
 * Get the current station's coordinates
 */
export function getCurrentStationCoords() {
    // Spot-based: spots carry their own coordinates (from data/spots.json)
    if (AppState.useSpotMode && AppState.currentSpot &&
        AppState.currentSpot.lat !== null && AppState.currentSpot.lat !== undefined &&
        AppState.currentSpot.lon !== null && AppState.currentSpot.lon !== undefined) {
        return {
            lat: AppState.currentSpot.lat,
            lon: AppState.currentSpot.lon
        };
    }
    if (!AppState.currentStation) {
        return DEFAULT_COORDS;
    }
    return {
        lat: AppState.currentStation.lat,
        lon: AppState.currentStation.lon
    };
}

/**
 * Get the current station's weather point coordinates
 */
export function getCurrentWeatherPoint() {
    if (!AppState.currentStation?.weatherPoint) {
        return DEFAULT_WEATHER_POINT;
    }
    return AppState.currentStation.weatherPoint;
}

/**
 * Get map configuration from current area
 */
export function getCurrentMapConfig() {
    if (!AppState.currentArea) {
        return { mapCenter: DEFAULT_MAP_CENTER, mapZoom: DEFAULT_MAP_ZOOM };
    }
    return {
        mapCenter: AppState.currentArea.mapCenter,
        mapZoom: AppState.currentArea.mapZoom
    };
}

/**
 * Determine which hierarchy levels should be collapsed
 * Returns info about what to show in UI
 */
export function getHierarchyVisibility() {
    // In spot mode, we use a flat spot picker instead of hierarchy
    if (AppState.useSpotMode) {
        const spotCount = AppState.spotsCache?.count || 0;
        return {
            showRegion: true,
            showArea: true,
            showStation: false,
            showSpot: true,
            regionCount: Object.keys(getGroupedSpots()).length,
            areaCount: 0,
            stationCount: 0,
            spotCount
        };
    }

    const regionCount = Object.keys(AppState.regions).length;
    const areaCount = AppState.currentRegion
        ? Object.keys(AppState.currentRegion.areas || {}).length
        : 0;
    const stationCount = AppState.currentArea
        ? Object.keys(AppState.currentArea.stations || {}).length
        : 0;

    return {
        showRegion: regionCount > 1,
        showArea: areaCount > 1,
        showStation: stationCount > 1,
        showSpot: false,
        regionCount,
        areaCount,
        stationCount,
        spotCount: 0
    };
}

/**
 * Set the current area and optionally a station
 * @param {Object} area - Area object from region.areas
 * @param {string} stationId - Optional station ID, defaults to area's default
 */
export function setCurrentArea(area, stationId = null) {
    AppState.useSpotMode = false;
    AppState.currentArea = area;

    // Use provided station or fall back to area's default
    const targetStationId = stationId || area.defaultStation;
    const station = area.stations[targetStationId];

    if (station) {
        AppState.currentStation = station;
        AppState.currentStationId = targetStationId;
    }
}

/**
 * Set the current region and optionally an area and station
 * @param {Object} region - Region object from regions.json
 * @param {string} areaId - Optional area ID, defaults to region's default
 * @param {string} stationId - Optional station ID, defaults to area's default
 */
export function setCurrentRegion(region, areaId = null, stationId = null) {
    AppState.useSpotMode = false;
    AppState.currentRegion = region;

    // Determine target area
    const areas = Object.keys(region.areas || {});
    const targetAreaId = areaId || region.defaultArea || areas[0];
    const area = region.areas[targetAreaId];

    if (area) {
        setCurrentArea(area, stationId);
    }
}

/**
 * Set the current station within the current area
 * @param {string} stationId - Station ID
 */
export function setCurrentStation(stationId) {
    if (!AppState.currentArea) {
        logger.warn('Cannot set station: no area selected');
        return false;
    }

    AppState.useSpotMode = false;
    const station = AppState.currentArea.stations[stationId];
    if (!station) {
        logger.warn(`Station ${stationId} not found in current area`);
        return false;
    }

    AppState.currentStation = station;
    AppState.currentStationId = stationId;
    return true;
}

/**
 * Attach legacy station context WITHOUT changing the navigation mode.
 *
 * Spot mode still needs the Region → Area → Station hierarchy populated
 * (tide-curve cache keys, map center/markers, weather fallbacks), but
 * setCurrentRegion/setCurrentArea/setCurrentStation all force
 * useSpotMode = false — calling them after setCurrentSpot silently killed
 * spot mode. This setter wires the hierarchy while leaving the flag alone.
 *
 * @param {string} regionId - Region ID (e.g., 'california')
 * @param {string} areaId - Area ID (e.g., 'san-diego-county')
 * @param {string} stationId - Station ID (e.g., '9410170')
 * @returns {boolean} Whether the full context was attached
 */
export function attachStationContext(regionId, areaId, stationId) {
    const region = AppState.regions?.[regionId];
    if (!region) {
        logger.warn(`attachStationContext: region ${regionId} not found`);
        return false;
    }

    const area = region.areas?.[areaId];
    if (!area) {
        logger.warn(`attachStationContext: area ${areaId} not found in ${regionId}`);
        return false;
    }

    AppState.currentRegion = region;
    AppState.currentArea = area;

    const station = area.stations?.[stationId];
    if (station) {
        AppState.currentStation = station;
        AppState.currentStationId = stationId;
    }

    return Boolean(station);
}

/**
 * Find the full hierarchy path for a station ID
 * Searches all regions and areas to find where a station lives
 * @param {string} stationId - Station ID to search for
 * @param {Object} regions - All regions object
 * @returns {Object|null} { regionId, areaId, stationId } or null if not found
 */
export function findLocationForStation(stationId, regions) {
    for (const [regionId, region] of Object.entries(regions)) {
        for (const [areaId, area] of Object.entries(region.areas || {})) {
            if (area.stations && area.stations[stationId]) {
                return { regionId, areaId, stationId };
            }
        }
    }
    return null;
}

/**
 * Resolve a legacy region ID to the new hierarchy
 * @param {string} legacyId - Old region ID (e.g., 'socal')
 * @returns {Object|null} { region, area } or null
 */
export function resolveLegacyRegion(legacyId) {
    const mapping = AppState.legacyMapping[legacyId];
    if (mapping && AppState.regions[mapping.region]) {
        return mapping;
    }
    return null;
}

/**
 * Initialize state with loaded regions data (legacy mode)
 * @param {Object} regionsData - The full regions.json data
 * @param {string} regionId - Initial region ID (optional)
 * @param {string} areaId - Initial area ID (optional)
 * @param {string} stationId - Initial station ID (optional)
 */
export function initializeState(regionsData, regionId = null, areaId = null, stationId = null) {
    AppState.useSpotMode = false;
    AppState.regions = regionsData.regions;
    AppState.legacyMapping = regionsData._legacyMapping || {};

    // Determine starting region
    let targetRegionId = regionId;
    let targetAreaId = areaId;

    // If station provided without region/area, try to find it
    if (stationId && !regionId) {
        const location = findLocationForStation(stationId, AppState.regions);
        if (location) {
            targetRegionId = location.regionId;
            targetAreaId = location.areaId;
        }
    }

    // Check for legacy region mapping
    if (targetRegionId && !AppState.regions[targetRegionId]) {
        const mapping = resolveLegacyRegion(targetRegionId);
        if (mapping) {
            targetRegionId = mapping.region;
            targetAreaId = targetAreaId || mapping.area;
        }
    }

    // Fall back to default region
    if (!targetRegionId || !AppState.regions[targetRegionId]) {
        targetRegionId = regionsData.defaultRegion || DEFAULTS.region;
    }

    const region = AppState.regions[targetRegionId];
    if (region) {
        setCurrentRegion(region, targetAreaId, stationId);
    }

    AppState.initialized = true;
}

/**
 * Initialize state with spot-based navigation (new mode)
 * @param {Object} spotsData - Spots data ({ spots: [...] } from data/spots.json
 *        or /api/get-conditions?list=spots)
 * @param {string} spotSlug - Initial spot slug (optional)
 */
export function initializeSpotState(spotsData, spotSlug = null) {
    AppState.useSpotMode = true;
    AppState.spotsCache = spotsData;

    const spots = spotsData?.spots || [];

    // If spot slug provided, verify it exists in the data
    if (spotSlug && spots.some(s => s.slug === spotSlug)) {
        AppState.currentSpotSlug = spotSlug;
    } else {
        // Explicit default spot from shared/defaults.mjs; fall back to the
        // first spot only if the default is somehow missing from the data.
        // (The old grouped.california['san-diego'] lookup used a key that
        // never existed — the real area key is 'san-diego-county' — so it
        // always silently fell through to spots[0].)
        const defaultSpot = spots.find(s => s.slug === DEFAULTS.spot);
        AppState.currentSpotSlug = defaultSpot?.slug || spots[0]?.slug || null;
    }

    AppState.initialized = true;
}

/**
 * Build URL params for the current location
 * Works for both spot and station modes
 */
export function buildLocationParams() {
    if (AppState.useSpotMode && AppState.currentSpotSlug) {
        return { spot: AppState.currentSpotSlug };
    }

    const params = {};
    if (AppState.currentRegion?.id) {
        params.region = AppState.currentRegion.id;
    }
    if (AppState.currentArea?.id) {
        params.area = AppState.currentArea.id;
    }
    if (AppState.currentStationId) {
        params.station = AppState.currentStationId;
    }
    return params;
}

/**
 * Parse URL params and return location info
 */
export function parseLocationParams(searchParams) {
    const spot = searchParams.get('spot');
    if (spot) {
        return { mode: 'spot', spot };
    }

    const region = searchParams.get('region');
    const area = searchParams.get('area');
    const station = searchParams.get('station');

    if (region || area || station) {
        return { mode: 'station', region, area, station };
    }

    return { mode: 'default' };
}
